import {
  ArrowLeftRight,
  BadgeCheck,
  Bell,
  Banknote,
  BarChart3,
  BookText,
  Boxes,
  CalendarDays,
  ClipboardCheck,
  DoorOpen,
  Images,
  FileCog,
  FileSignature,
  FileText,
  GraduationCap,
  KeyRound,
  LayoutDashboard,
  ListTree,
  Mail,
  Megaphone,
  MessageSquare,
  NotebookPen,
  PackageOpen,
  School,
  Settings,
  Sparkles,
  Tags,
  Users,
  UsersRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Item disembunyikan bila pengguna tidak punya izin ini. */
  permission?: string;

  /**
   * Halaman pengelolaan satuan — disembunyikan dari guru kelas walau izinnya
   * ada.
   *
   * Guru memegang `studyGroup.read` dan `school.read` bukan untuk mengurus
   * rombel dan profil lembaga, melainkan karena layar akademiknya butuh
   * keduanya sebagai data pendukung: pemilih rombel di Kehadiran dan Jurnal,
   * kop lembaga di lembar cetak. Mencabut izinnya akan mematikan layar-layar
   * itu; menyembunyikan menunya menyelesaikan hal yang sebenarnya keliru —
   * halamannya yang muncul, bukan izinnya yang ada.
   */
  notForClassTeacher?: boolean;

  /**
   * Hanya untuk guru kelas yang mengampu rombel jenjang tamat.
   *
   * SKTB tidak diterbitkan di KB Kecil maupun TK A, jadi bagi gurunya menu ini
   * bukan sekadar tidak terpakai — ia menyesatkan, karena mengesankan ada
   * surat yang seharusnya ia urus. Pengelola satuan tetap melihatnya:
   * jangkauannya seluruh rombel, termasuk yang tamat.
   */
  graduatingOnly?: boolean;

  /**
   * Alamat lain yang ikut menyalakan butir ini di sidebar — untuk menu yang
   * menjadi pintu satu alur, supaya ia tetap tersorot di langkah mana pun.
   * Dicocokkan persis, bukan awalan: `/rapor-penilaian` tidak boleh ikut
   * menyalakan Rapor di `/rapor-penilaian/rencana` milik Rencana Pembelajaran.
   */
  activePaths?: string[];
}

/**
 * Beberapa halaman sejenis di bawah satu induk yang bisa dibuka-tutup.
 *
 * Dipakai bila satu jenis pekerjaan sudah punya terlalu banyak halaman untuk
 * dijejer lurus di sidebar. Induknya tidak punya alamat sendiri — ia cuma
 * pembuka daftar, sehingga tidak ada halaman hub yang perlu dibuat dan tidak
 * ada alamat lama yang berubah.
 */
export interface NavGroup {
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

export type NavEntry = NavItem | NavGroup;

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

export interface NavSection {
  label: string;
  items: NavEntry[];
}

/**
 * Struktur menu dashboard tenant, mengikuti pengelompokan modul di PRD.
 *
 * Menu difilter berdasarkan permission: item yang tidak bisa diakses
 * tidak ditampilkan sama sekali, bukan ditampilkan lalu ditolak saat
 * diklik.
 */
export const NAVIGATION: NavSection[] = [
  {
    label: 'Umum',
    items: [{ label: 'Dashboard', href: '/', icon: LayoutDashboard, permission: 'dashboard.read' }],
  },
  {
    label: 'Master Data',
    items: [
      {
        label: 'Data Sekolah',
        href: '/sekolah',
        icon: School,
        permission: 'school.read',
        notForClassTeacher: true,
      },
      {
        label: 'Tahun Ajaran',
        href: '/tahun-ajaran',
        icon: CalendarDays,
        permission: 'academicYear.read',
        notForClassTeacher: true,
      },
      {
        label: 'Rombel',
        href: '/kelompok',
        icon: UsersRound,
        permission: 'studyGroup.read',
        notForClassTeacher: true,
      },
      { label: 'Data PTK', href: '/guru', icon: GraduationCap, permission: 'teacher.read' },
      {
        label: 'Akun PTK',
        href: '/guru/akun',
        icon: KeyRound,
        permission: 'teacher.read',
      },
      { label: 'Peserta Didik', href: '/peserta-didik', icon: Users, permission: 'student.read' },
      {
        label: 'Buku Induk',
        href: '/peserta-didik/buku-induk',
        icon: BookText,
        permission: 'student.read',
      },
      {
        label: 'Mutasi Siswa',
        href: '/peserta-didik/mutasi',
        icon: ArrowLeftRight,
        permission: 'student.read',
      },
      { label: 'Orang Tua', href: '/orang-tua', icon: Users, permission: 'parent.read' },
      {
        label: 'Akun Wali Murid',
        href: '/orang-tua/akun',
        icon: KeyRound,
        permission: 'parent.read',
      },
      {
        label: 'Setelan Portal Wali',
        // Menulis ke data satuan (sakelar tampil/sembunyi), bukan ke data orang
        // tua — yang boleh membukanya adalah yang boleh mengubah Sekolah, sama
        // seperti Setelan Rapor & Setelan SKTB.
        href: '/orang-tua/portal',
        icon: FileCog,
        permission: 'school.update',
      },
    ],
  },
  {
    label: 'Akademik',
    items: [
      {
        // Urutan menu Akademik mengikuti urutan kerja guru sepanjang semester:
        // merencanakan dulu (Rencana Pembelajaran, Kokurikuler), lalu mencatat
        // harian (Kehadiran, Jurnal), baru melapor (Rapor), dan terakhir SKTB
        // yang hanya menyangkut rombel yang tamat.
        //
        // TP, ATP, dan Aktivitas dulunya tiga menu terpisah, padahal bagi guru
        // ketiganya satu pekerjaan: dari Elemen pilih TP, TP terpilih menjadi
        // alurnya (ATP), lalu tiap TP diisi Aktivitas — kini satu layar.
        label: 'Rencana Pembelajaran',
        href: '/rapor-penilaian/rencana',
        icon: ListTree,
        permission: 'learningObjectiveFlow.read',
      },
      {
        label: 'Kokurikuler',
        href: '/kokurikuler',
        icon: Sparkles,
        permission: 'cocurricular.read',
      },
      {
        label: 'Portofolio',
        href: '/portofolio',
        icon: Images,
        permission: 'portfolio.read',
      },
      {
        label: 'Kehadiran',
        href: '/kehadiran',
        icon: ClipboardCheck,
        permission: 'attendance.read',
      },
      {
        // Diletakkan tepat sesudah Kehadiran: jurnal mengambil rekap kehadiran
        // hari itu, jadi urutan mengisinya memang kehadiran dulu baru jurnal.
        label: 'Jurnal Mengajar',
        href: '/jurnal',
        icon: NotebookPen,
        permission: 'teachingJournal.read',
      },
      {
        // Izinnya `verify`, bukan `teachingJournal.read`: layar ini memaparkan
        // kelalaian pengisian seluruh guru, dan itu bahan supervisi kepala
        // satuan — bukan papan pengumuman untuk sesama guru.
        label: 'Supervisi Jurnal',
        href: '/jurnal/supervisi',
        icon: ClipboardCheck,
        permission: 'teachingJournal.verify',
      },
      {
        // Satu pintu untuk seluruh Rapor, seperti SKTB di bawahnya. Penilaian
        // (intrakurikuler & kokurikuler) dan Setelan Rapor tidak lagi menjadi
        // menu sendiri — langkah-langkahnya dicapai lewat stepper di atas
        // halaman, lihat `@/config/workflows`. Rencana Pembelajaran,
        // Kokurikuler, dan Kehadiran tetap menu sendiri karena dikerjakan
        // sepanjang semester, bukan hanya menjelang rapor.
        label: 'Rapor',
        href: '/rapor',
        icon: FileText,
        permission: 'reportCard.read',
        activePaths: ['/rapor-penilaian', '/rapor-penilaian/kokurikuler'],
      },
      {
        // Satu pintu untuk seluruh SKTB. Empat halamannya (Setelan, Calon
        // Kelulusan, Penilaian, Cetak) dulu dijejer sebagai empat menu, dan
        // urutan mengerjakannya tidak terbaca dari sidebar. Kini menu ini
        // membuka Calon Kelulusan, dan langkah lainnya dicapai lewat stepper
        // di atas halaman — lihat `@/config/workflows`. Hak lihat tiap
        // langkahnya tetap dipegang langkah itu sendiri.
        label: 'SKTB',
        href: '/sktb/calon-kelulusan',
        icon: GraduationCap,
        // Modul `sktb` sendiri, lepas dari `reportCard` — SKTB dan Rapor
        // adalah dua fitur paket yang bisa dibeli terpisah.
        permission: 'sktb.read',
        graduatingOnly: true,
        activePaths: ['/sktb/setelan', '/sktb/cetak', '/penilaian'],
      },
    ],
  },
  {
    label: 'Administrasi',
    items: [
      {
        // Tiap template surat berdiri sendiri sebagai halaman, tapi di menu
        // ketiganya melipat ke satu induk: nama lengkapnya panjang-panjang dan
        // berawalan sama, sehingga dijejer lurus ia memenuhi sidebar tanpa
        // menambah keterangan apa pun. Alamat tiap halaman tidak berubah.
        label: 'Surat',
        icon: Mail,
        items: [
          {
            label: 'Keterangan Aktif',
            href: '/surat/keterangan-aktif',
            icon: Mail,
            permission: 'letter.read',
          },
          {
            label: 'Menerima Pindahan',
            href: '/surat/menerima-pindahan',
            icon: Mail,
            permission: 'letter.read',
          },
          {
            label: 'Pengajuan Approval Dapodik',
            href: '/surat/pengajuan-dapodik',
            icon: Mail,
            permission: 'letter.read',
          },
        ],
      },
      {
        // Satu halaman untuk seluruh jenis SK — pembagian tugas dan panitia
        // SPMB dipilih di dalamnya. Ikut fitur & izin Surat Menyurat, bukan
        // modul izin tersendiri: ia sama-sama produk persuratan satuan.
        label: 'SK',
        href: '/sk',
        icon: FileSignature,
        permission: 'letter.read',
      },
      {
        label: 'Pengumuman',
        href: '/pengumuman',
        icon: Megaphone,
        permission: 'announcement.read',
      },
      {
        // Tanpa permission: yang menerima notifikasi adalah penerbit
        // pengumuman, dan itu sering guru yang tidak punya notification.read.
        // Izin itu hanya membuka tab "Seluruh Lembaga" di dalam halamannya.
        label: 'Notifikasi',
        href: '/notifikasi',
        icon: Bell,
      },
      { label: 'Kalender', href: '/kalender', icon: CalendarDays, permission: 'calendar.read' },
    ],
  },
  {
    label: 'Keuangan',
    items: [
      { label: 'Tagihan', href: '/tagihan', icon: Wallet, permission: 'invoice.read' },
      { label: 'Pembayaran', href: '/pembayaran', icon: Banknote, permission: 'payment.read' },
      {
        // Tepat sesudah Pembayaran: keduanya berujung pada baris kwitansi yang
        // sama, bedanya di sini uangnya belum tentu masuk — bendahara yang
        // memutuskan. Lihat model PaymentConfirmation untuk alasan keduanya
        // tidak digabung jadi satu daftar.
        label: 'Konfirmasi Pembayaran',
        href: '/pembayaran/konfirmasi',
        icon: BadgeCheck,
        permission: 'paymentConfirmation.read',
      },
      {
        label: 'Setelan Kwitansi',
        href: '/pembayaran/kwitansi-setelan',
        icon: FileCog,
        // Menulis ke data satuan (judul & penanda tangan lembar), bukan ke
        // pembayaran — sederet dengan Setelan Rapor & Setelan SKTB.
        permission: 'receiptSetting.read',
      },
      { label: 'Pengeluaran', href: '/pengeluaran', icon: Banknote, permission: 'expense.read' },
      {
        label: 'Laporan Keuangan',
        href: '/laporan-keuangan',
        icon: BarChart3,
        permission: 'financeReport.read',
      },
    ],
  },
  {
    label: 'Inventaris',
    items: [
      { label: 'Barang', href: '/inventaris', icon: Boxes, permission: 'item.read' },
      {
        label: 'Buku Mutasi',
        href: '/inventaris/mutasi',
        icon: ArrowLeftRight,
        permission: 'itemMutation.read',
      },
      {
        // Persediaan menumpang izin modul `item`: barang habis pakai adalah
        // bagian dari pekerjaan yang sama, dan RBAC sengaja tidak dipecah
        // untuknya (lihat ConsumableController).
        label: 'Barang Habis Pakai',
        href: '/inventaris/persediaan',
        icon: PackageOpen,
        permission: 'item.read',
      },
      { label: 'Ruangan', href: '/inventaris/ruangan', icon: DoorOpen, permission: 'room.read' },
      {
        label: 'Kategori Barang',
        href: '/inventaris/kategori',
        icon: Tags,
        permission: 'itemCategory.read',
      },
      {
        label: 'Laporan & Cetak',
        href: '/inventaris/laporan',
        icon: BarChart3,
        permission: 'item.read',
      },
    ],
  },
  {
    label: 'Komunikasi',
    items: [
      { label: 'Broadcast', href: '/broadcast', icon: MessageSquare, permission: 'broadcast.read' },
      {
        // Menyimpan kredensial langganan lembaga di gateway, bukan mengirim
        // pesan — yang boleh membukanya adalah yang boleh mengubah Pengaturan,
        // sama seperti Setelan Rapor & Setelan Portal Wali.
        label: 'Setelan Broadcast',
        href: '/broadcast/setelan',
        icon: FileCog,
        permission: 'setting.update',
      },
    ],
  },
  {
    label: 'Sistem',
    items: [
      { label: 'Pengguna', href: '/pengguna', icon: Users, permission: 'user.read' },
      {
        // Langganan lembaga kepada SIPAUD — jangan tertukar dengan menu
        // Tagihan di bawah Keuangan, yang berisi tagihan lembaga kepada wali
        // murid. Dijaga `billing.read`, izin yang hanya dipegang pengelola
        // lembaga: guru tidak perlu melihat harga paket sekolahnya.
        label: 'Langganan',
        href: '/langganan',
        icon: BadgeCheck,
        permission: 'billing.read',
      },
      { label: 'Pengaturan', href: '/pengaturan', icon: Settings, permission: 'setting.read' },
    ],
  },
];
